import React, { useRef, useState } from 'react';
import '@/assets/styles/components/_video-upload.styles.scss';
import { LoadingSpinner } from '@/components';
import { IVideoUploadProps } from './video-upload.types.ts';
import { useDocumentUpload } from '@/hooks/use-document-upload.tsx';
import { useQueryClient } from 'react-query';
import { formatCustomDate } from '@/utility';
import ReactPlayer from 'react-player';
import Modal from '@/components/modal/Modal.tsx';

const loadingText: string = 'Videolarınız kaydediliyor.<br />Beklediğiniz için teşekkür ederiz.';

export const VideoUpload: React.FC<IVideoUploadProps> = ({ videoUploads = [] }) => {
	const queryClient = useQueryClient();
	const { uploadDocument } = useDocumentUpload();
	const fileInputRefs = useRef<Array<HTMLInputElement | null>>([]);
	const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
	const [selectedVideoTitle, setSelectedVideoTitle] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [playError, setPlayError] = useState<string | null>(null);

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, documentTypeId: number) => {
		const file = event.target.files?.[0];
		if (file) {
			setIsUploading(true);
			try {
				await uploadDocument(file, 'VIDEO', documentTypeId);
				queryClient.refetchQueries({ queryKey: ['getDocumentsByContentType'] });
			} finally {
				setIsUploading(false);
			}
		}
	};

	const handleVideoClick = (videoUrl: string, documentTypeName: string) => {
		if (!videoUrl) {
			console.error('Video URL is missing');
			return;
		}
		setSelectedVideo(videoUrl);
		setSelectedVideoTitle(documentTypeName);
		setIsModalOpen(true);
		setPlayError(null);
	};

	const handleVideoError = () => {
		setPlayError('Video yüklenirken bir hata oluştu.');
	};

	const isMobile = window.innerWidth <= 768;

	return (
		<div className="video-uploads">
			{isUploading && <LoadingSpinner loadingText={loadingText} />}

			{videoUploads.map((upload, index) => (
				<div
					key={index}
					className={`video-item ${upload.isUploaded ? 'video-uploaded' : ''}`}>
					{upload.isUploaded && isMobile ? (
						// Mobil için özel yüklenmiş video tasarımı
						<div className="mobile-video-success">
							<div
								className="mobile-video-thumbnail"
								onClick={() => handleVideoClick(upload.documentUrl!, upload.documentTypeName || '')}>
								<video
									ref={el => (videoRefs.current[index] = el)}
									src={upload.documentUrl}
									width="75"
									height="75"
									onError={handleVideoError}
									preload="metadata"
									className="mobile-video-preview"
								/>
								<div className="play-icon">
									<i className="icons-play-circle" />
								</div>
							</div>
							<div className="mobile-video-info">
								<h3>{upload.documentTypeName}</h3>
								<p className="upload-success-message">Video başarıyla yüklendi!</p>
							</div>
						</div>
					) : (
						// Normal masaüstü tasarımı
						<>
							<div className="video-icon">
								{upload.isUploaded ? (
									<img
										src="assets/camera-upload.svg"
										alt="Kamera Video Yüklendi"
									/>
								) : (
									<img
										src="assets/camera.svg"
										alt="Kamera"
									/>
								)}
							</div>
							<div className="content">
								<div className="title-row">
									<div className="title-content">
										<h3>{upload.documentTypeName}</h3>
										{!upload?.isUploaded && (
											<p className="description">
												{upload.documentTypeId === 13
													? 'Kendini tanıttığın 2 dakikalık bir video kaydı alın ve buraya yükleyin. Bu işlemi sadece bir kez yapabilirsiniz.'
													: '"Motivasyon Türleri Nedir?" konulu 3 dakikalık video kaydı alın ve buraya yükleyin. Bu işlemi sadece bir kez yapabilirsiniz.'}
											</p>
										)}
									</div>
								</div>
								{!upload?.isUploaded && (
									<button
										className="upload-button"
										onClick={() => fileInputRefs.current[index]?.click()}>
										Dosya Yükle
									</button>
								)}
								{upload.isUploaded && <div className="success-message">Video başarıyla yüklendi!</div>}
								<input
									type="file"
									accept="video/*"
									className="hidden"
									ref={el => (fileInputRefs.current[index] = el)}
									onChange={e => handleFileUpload(e, upload.documentTypeId!)}
								/>
							</div>
							{upload.isUploaded && upload.documentUrl && (
								<div className="success-detail">
									<div
										className="video-thumbnail"
										onClick={() => handleVideoClick(upload.documentUrl!, upload.documentTypeName || '')}>
										<video
											ref={el => (videoRefs.current[index] = el)}
											src={upload.documentUrl}
											width="150"
											height="100"
											onError={handleVideoError}
											preload="metadata"
											className="object-cover rounded-lg"
										/>
										<div className="play-overlay">
											<i className="icons-play-circle" />
										</div>
										{playError && <div className="error-message">{playError}</div>}
									</div>

									{upload.uploadDate && <div className="timestamp">{formatCustomDate(upload.uploadDate)}</div>}
								</div>
							)}
						</>
					)}
				</div>
			))}

			<Modal
				size="lg"
				isOpen={isModalOpen}
				showCloseButton
				variant="centered"
				className="video-modal-container"
				contentClassName="video-upload-modal-content"
				onClose={() => setIsModalOpen(false)}>
				<h3 className="video-modal-title">{selectedVideoTitle}</h3>
				<div className="video-modal">
					{selectedVideo && (
						<ReactPlayer
							url={selectedVideo}
							width="100%"
							height="auto"
							controls={true}
							playing={true}
							onError={handleVideoError}
						/>
					)}
					{playError && <div className="error-message">{playError}</div>}
				</div>
			</Modal>
		</div>
	);
};

export default VideoUpload;
